import { useCallback, useMemo } from "react";
import Separator from "@/app/components/lib/Separator";
import {
  unit,
  UnitSelectionStyle,
} from "@/api/AppApi/services/product/interfaces/api-product-details-interface";
import { useTranslation } from "@/hooks/localization/useTranslations";
import OptionsDropdown from "@/app/components/common-addons-builder/common-addons-fields/hook-dropdown/options-dropdown/OptionsDropdown";
import CheckboxOption from "@/app/components/common-filter-builder/common-filters-components/CheckboxOption";
import TabsSlider from "@/app/components/lib/Embla-tabs-slider/TabsSlider";
import ButtonOption from "@/app/components/common-filter-builder/common-filters-components/ButtonOption";
import { Col, Row } from "@/utils/bootstrap";

type TProps = {
  units: unit[];
  onSelect: (selectedUnit: unit) => void;
  selectedUnitId: unit["id"];
};

const Units: React.FC<TProps> = ({ onSelect, units, selectedUnitId }) => {
  const t = useTranslation();

  const selectedUnit = useMemo(
    () => units.find(({ id }) => id === selectedUnitId),
    [units, selectedUnitId],
  );

  const selectionStyle = useMemo(
    () => units[0]?.selection_type || UnitSelectionStyle.dropdown,
    [units],
  );

  const handleSelect = useCallback(
    (unitItem: unit) => () => {
      onSelect(unitItem);
    },
    [onSelect],
  );

  if (units.length < 2) return null;

  const renderCarousel = () => (
    <>
      <Separator startLabel={t.unit} marginBottom={8} />
      <TabsSlider
        spacing={8}
        selectedIndex={units.findIndex((u) => u.id === selectedUnitId)}
      >
        {units.map((unitItem) => (
          <ButtonOption
            key={unitItem.id}
            text={unitItem.name}
            selected={unitItem.id === selectedUnitId}
            toggleOption={handleSelect(unitItem)}
          />
        ))}
      </TabsSlider>
    </>
  );

  const renderTag = () => (
    <>
      <Separator startLabel={t.unit} marginBottom={8} />
      <Row
        as="ul"
        style={{
          columnGap: 16,
          rowGap: 8,
          padding: 0,
          margin: 0,
          listStyleType: "none",
        }}
        className="tags-options"
      >
        {units.map((unitItem) => (
          <Col as="li" className="p-0 col-auto" key={unitItem.id}>
            <ButtonOption
              text={unitItem.name}
              selected={unitItem.id === selectedUnitId}
              toggleOption={handleSelect(unitItem)}
            />
          </Col>
        ))}
      </Row>
    </>
  );

  const renderDropdown = () => (
    <>
      <Separator startLabel={t.unit} marginBottom={-20} />
      <OptionsDropdown title={selectedUnit?.name ?? t.choose_from_list}>
        {units.map((unitItem) => (
          <CheckboxOption
            key={unitItem.id}
            label={unitItem.name}
            disabled={unitItem.id === selectedUnitId}
            checked={unitItem.id === selectedUnitId}
            toggleOption={handleSelect(unitItem)}
          />
        ))}
      </OptionsDropdown>
    </>
  );

  switch (selectionStyle) {
    case UnitSelectionStyle.CAROUSEL:
      return renderCarousel();
    case UnitSelectionStyle.TAG:
      return renderTag();
    case UnitSelectionStyle.dropdown:
    default:
      return renderDropdown();
  }
};

export default Units;
